<template>
  <div>
    <div class="row mb-2 pe-4">
      <div class="col-lg-8 col-md-7 col-7 d-flex align-items-center">
        <div class="color-common">
          <h3 class="title-page mb-0 ps-3">Export / Import merchant settings</h3>
        </div>
      </div>
    </div>
    <div class="page-header align-items-start min-vh-100">
      <span class="mask bg-white"></span>
      <div class="container">
        <div class="row">
          <div class="col-lg-12 col-md-12 col-12 mx-auto mt-3">
            <h4 class="title-page">Export</h4>
            <div class="d-flex flex-wrap align-items-center mb-2">
              <material-button
                @click="exportSettingsJson()"
                class="btn btn-sm btn-background mw-185px me-3"
              >
                Export JSON
              </material-button>
              <material-button
                @click="exportSettingsImages()"
                class="btn btn-sm btn-background mw-185px me-3"
              >
                Export images
              </material-button>
            </div>

            <h4 class="title-page mt-5">Import</h4>
            <div class="d-flex mt-4 s-setting align-items-center">
              <div class="me-3">File</div>
              <material-input
                hidden
                type="file"
                id="file_import_settings_json"
                name="file_import_settings_json"
                @change="onFileChange($event)"
                accept=".json"
              />
              <material-button
                @click="chooseFile()"
                class="btn btn-sm btn-background mw-185px me-3"
              >
                Choose file
              </material-button>
              <div>{{ fileName }}</div>
            </div>
            <div class="d-flex mt-2 s-setting" v-if="fileError">
              <div class="me-3"></div>
              <div class="is-invalid">{{ fileError }}</div>
            </div>

            <div class="d-flex mt-4 s-setting align-items-center">
              <div class="me-3">Pictures</div>
              <material-input
                hidden
                type="file"
                id="file_images_settings_zip"
                name="file_images_settings_zip"
                @change="onImagesChange($event)"
                accept=".zip"
              />
              <material-button
                @click="chooseImages()"
                class="btn btn-sm btn-background mw-185px me-3"
              >
                Choose .zip
              </material-button>
              <div>{{ imagesName }}</div>
            </div>
            <div class="d-flex mt-2 s-setting" v-if="imagesError">
              <div class="me-3"></div>
              <div class="is-invalid">{{ imagesError }}</div>
            </div>

            <div class="mt-5 d-flex flex-wrap align-items-center">
              <material-button
                @click="preview()"
                class="btn btn-sm btn-background mw-185px me-3"
              >
                Preview
              </material-button>
              <!-- Import does not wait for a preview. The server validates the file again and
                   writes in one transaction, so gating the button only made it look broken. -->
              <material-button
                @click="confirmImport()"
                class="btn btn-sm btn-background mw-185px"
              >
                Import
              </material-button>
            </div>
          </div>

          <div class="col-lg-12 col-md-12 col-12 mx-auto mt-5" v-if="previewResult">
            <h4 class="title-page">What will change</h4>
            <div class="text-sm text-secondary mb-3" v-if="previewResult.source">
              From merchant <b>{{ previewResult.source.merchant_code || "-" }}</b>
              <span v-if="previewResult.exported_at">, exported {{ previewResult.exported_at }}</span>
            </div>
            <table class="table table-sm w-auto mb-4">
              <tbody>
                <tr v-for="(value, key) in previewResult.counts" :key="key">
                  <td class="text-sm ps-0 pe-4">{{ label(key) }}</td>
                  <td class="text-sm text-end">{{ value }}</td>
                </tr>
                <tr v-if="previewResult.kiosk">
                  <td class="text-sm ps-0 pe-4">Kiosk branches</td>
                  <td class="text-sm text-end">{{ previewResult.kiosk.counts.branches }}</td>
                </tr>
                <tr v-if="previewResult.grabfood">
                  <td class="text-sm ps-0 pe-4">GrabFood stores</td>
                  <td class="text-sm text-end">{{ previewResult.grabfood.counts.stores }}</td>
                </tr>
                <tr v-for="section in rowSections" :key="`count-${section.key}`">
                  <td class="text-sm ps-0 pe-4">{{ section.title }}</td>
                  <td class="text-sm text-end">{{ section.section.counts.rows }}</td>
                </tr>
              </tbody>
            </table>

            <div v-if="previewResult.changed.length" class="mb-4">
              <h5 class="text-sm fw-bold">Changed</h5>
              <div class="table-responsive">
                <table class="table table-sm">
                  <thead>
                    <tr>
                      <th class="text-sm ps-0">Setting</th>
                      <th class="text-sm">Now</th>
                      <th class="text-sm">After import</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-for="row in previewResult.changed" :key="row.key">
                      <td class="text-sm ps-0">{{ row.key }}</td>
                      <td class="text-sm text-secondary">{{ row.current }}</td>
                      <td class="text-sm">{{ row.new }}</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>

            <div v-if="previewResult.added.length" class="mb-4">
              <h5 class="text-sm fw-bold">Added</h5>
              <div class="table-responsive">
                <table class="table table-sm">
                  <tbody>
                    <tr v-for="row in previewResult.added" :key="row.key">
                      <td class="text-sm ps-0">{{ row.key }}</td>
                      <td class="text-sm">{{ row.new }}</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>

            <div v-if="previewResult.kiosk" class="mb-4">
              <h5 class="text-sm fw-bold">Kiosk, by branch code</h5>
              <div class="text-sm text-secondary" v-if="kioskUnchanged">
                {{ kioskUnchanged }} branch(es) already match the file and are not listed.
              </div>
              <div class="table-responsive" v-if="kioskRows.length">
                <table class="table table-sm">
                  <tbody>
                    <tr v-for="row in kioskRows" :key="row.branch_code">
                      <td class="text-sm ps-0">{{ row.branch_code }}</td>
                      <td class="text-sm" :class="{ 'text-warning': row.status === 'skipped' }">
                        <template v-if="row.status === 'skipped'">{{ row.reason }}</template>
                        <template v-else>{{ row.changed.join(", ") }}</template>
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>

            <div
              v-if="previewResult.grabfood && previewResult.grabfood.stores.length"
              class="mb-4"
            >
              <h5 class="text-sm fw-bold">GrabFood stores, by branch code</h5>
              <div class="table-responsive">
                <table class="table table-sm">
                  <tbody>
                    <tr v-for="row in previewResult.grabfood.stores" :key="row.branch_code">
                      <td class="text-sm ps-0">{{ row.branch_code }}</td>
                      <td class="text-sm" :class="{ 'text-warning': row.status === 'skipped' }">
                        <template v-if="row.status === 'skipped'">{{ row.reason }}</template>
                        <template v-else>{{ row.status }} — {{ row.grab_store_id }}</template>
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>

            <div
              v-for="section in rowSections"
              :key="section.key"
              class="mb-4"
            >
              <h5 class="text-sm fw-bold">{{ section.title }}, by {{ section.by }}</h5>
              <div class="text-sm text-secondary" v-if="section.unchanged">
                {{ section.unchanged }} already match the file and are not listed.
              </div>
              <div class="table-responsive" v-if="section.rows.length">
                <table class="table table-sm row-section">
                  <tbody>
                    <tr v-for="row in section.rows" :key="row.key">
                      <td class="text-sm ps-0">{{ row.key }}</td>
                      <td class="text-sm">
                        {{ row.status }}
                        <template v-if="row.changed && row.changed.length">
                          — {{ row.changed.join(", ") }}
                        </template>
                      </td>
                      <td class="text-sm text-warning">{{ row.note }}</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>

            <div v-if="previewResult.skipped.length" class="mb-4">
              <h5 class="text-sm fw-bold">Skipped</h5>
              <div
                v-for="row in previewResult.skipped"
                :key="row.key"
                class="text-sm text-warning"
              >
                {{ row.key }} — {{ row.reason }}
              </div>
            </div>
          </div>

          <div class="col-lg-12 col-md-12 col-12 mx-auto mt-5">
            <hr />
            <h4 class="title-page">Export Sales Channel</h4>
            <div class="d-flex flex-wrap align-items-center mb-2">
              <material-button
                @click="exportBranchesJson()"
                class="btn btn-sm btn-background mw-185px me-3"
              >
                Export branches
              </material-button>
              <material-button
                @click="exportBranchesImages()"
                class="btn btn-sm btn-background mw-185px me-3"
              >
                Export branch icons
              </material-button>
            </div>

            <h4 class="title-page mt-5">Import Sales Channel</h4>

            <div class="d-flex mt-4 s-setting align-items-center">
              <div class="me-3">File</div>
              <material-input
                hidden
                type="file"
                id="file_import_branches_json"
                name="file_import_branches_json"
                @change="onBranchFileChange($event)"
                accept=".json"
              />
              <material-button
                @click="chooseBranchFile()"
                class="btn btn-sm btn-background mw-185px me-3"
              >
                Choose file
              </material-button>
              <div>{{ branchFileName }}</div>
            </div>
            <div class="d-flex mt-2 s-setting" v-if="branchFileError">
              <div class="me-3"></div>
              <div class="is-invalid">{{ branchFileError }}</div>
            </div>

            <div class="d-flex mt-4 s-setting align-items-center">
              <div class="me-3">Icons</div>
              <material-input
                hidden
                type="file"
                id="file_images_branches_zip"
                name="file_images_branches_zip"
                @change="onBranchImagesChange($event)"
                accept=".zip"
              />
              <material-button
                @click="chooseBranchImages()"
                class="btn btn-sm btn-background mw-185px me-3"
              >
                Choose .zip
              </material-button>
              <div>{{ branchImagesName }}</div>
            </div>
            <div class="d-flex mt-2 s-setting" v-if="branchImagesError">
              <div class="me-3"></div>
              <div class="is-invalid">{{ branchImagesError }}</div>
            </div>

            <div class="mt-5 d-flex flex-wrap align-items-center">
              <material-button
                @click="previewBranches()"
                class="btn btn-sm btn-background mw-185px me-3"
              >
                Preview
              </material-button>
              <material-button
                @click="confirmImportBranches()"
                class="btn btn-sm btn-background mw-185px"
              >
                Import
              </material-button>
            </div>
          </div>

          <div class="col-lg-12 col-md-12 col-12 mx-auto mt-5" v-if="branchPreviewResult">
            <h4 class="title-page">What will change — Sales Channel</h4>
            <div class="text-sm text-secondary mb-3" v-if="branchPreviewResult.source">
              From merchant <b>{{ branchPreviewResult.source.merchant_code || "-" }}</b>
              <span v-if="branchPreviewResult.exported_at">
                , exported {{ branchPreviewResult.exported_at }}
              </span>
            </div>
            <div
              v-for="section in branchSections"
              :key="section.key"
              class="mb-4"
            >
              <h5 class="text-sm fw-bold">{{ section.title }}, by {{ section.by }}</h5>
              <div class="text-sm text-secondary" v-if="section.unchanged">
                {{ section.unchanged }} already match the file and are not listed.
              </div>
              <div class="table-responsive" v-if="section.rows.length">
                <table class="table table-sm row-section">
                  <tbody>
                    <tr v-for="row in section.rows" :key="row.key">
                      <td class="text-sm ps-0">{{ row.key }}</td>
                      <td class="text-sm">
                        {{ row.status }}
                        <template v-if="row.changed && row.changed.length">
                          — {{ row.changed.join(", ") }}
                        </template>
                      </td>
                      <td class="text-sm text-warning">{{ row.note }}</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          </div>

          <div class="col-lg-12 col-md-12 col-12 mx-auto mt-5" v-if="branchImportResult">
            <h4 class="title-page">Imported — Sales Channel</h4>
            <div class="text-sm mb-3">
              <b>{{ branchImportResult.branches }}</b> branch(es) written,
              <b>{{ branchImportResult.created }}</b> of them new and switched off.
              <span v-if="branchImportResult.physical_branches">
                <b>{{ branchImportResult.physical_branches }}</b> grouping(s) written.
              </span>
            </div>
            <div v-if="branchImportResult.skipped && branchImportResult.skipped.length">
              <h5 class="text-sm fw-bold">Skipped</h5>
              <div
                v-for="row in branchImportResult.skipped"
                :key="row.key"
                class="text-sm text-warning"
              >
                {{ row.key }} — {{ row.reason }}
              </div>
            </div>
          </div>

          <div class="col-lg-12 col-md-12 col-12 mx-auto mt-5" v-if="importResult">
            <h4 class="title-page">Imported</h4>
            <div class="text-sm mb-3">
              <b>{{ importResult.imported }}</b> settings written,
              <b>{{ importResult.changed }}</b> of them different from before.
              <span v-if="importResult.kiosk_branches">
                Kiosk setup written on <b>{{ importResult.kiosk_branches }}</b> branch(es).
              </span>
              <span v-if="importResult.grab_stores">
                GrabFood mapping written for <b>{{ importResult.grab_stores }}</b> store(s).
              </span>
              <span v-for="written in importedRows" :key="written.key">
                <b>{{ written.count }}</b> {{ written.title }} written.
              </span>
            </div>
            <div v-if="importResult.skipped && importResult.skipped.length">
              <h5 class="text-sm fw-bold">Skipped</h5>
              <div
                v-for="row in importResult.skipped"
                :key="row.key"
                class="text-sm text-warning"
              >
                {{ row.key }} — {{ row.reason }}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
  <!-- The two exports are POST form submits so the browser saves the response as a file; the
       token travels in the body, which is why they are not plain links. -->
  <form :action="`${actionDownload}/merchant/merchant-setting/export-json`" method="POST">
    <input type="hidden" name="merchantId" :value="merchantIdExport" />
    <input type="hidden" name="merchant_code" :value="merchantCodeExport" />
    <input type="hidden" name="token" :value="token" />
    <input id="exportSettingsJson" type="submit" class="d-none" />
  </form>
  <form :action="`${actionDownload}/merchant/merchant-setting/export-json-images`" method="POST">
    <input type="hidden" name="merchantId" :value="merchantIdExport" />
    <input type="hidden" name="merchant_code" :value="merchantCodeExport" />
    <input type="hidden" name="token" :value="token" />
    <input id="exportSettingsImages" type="submit" class="d-none" />
  </form>
  <form :action="`${actionDownload}/merchant/branches/export-json`" method="POST">
    <input type="hidden" name="merchantId" :value="merchantIdExport" />
    <input type="hidden" name="merchant_code" :value="merchantCodeExport" />
    <input type="hidden" name="token" :value="token" />
    <input id="exportBranchesJson" type="submit" class="d-none" />
  </form>
  <form :action="`${actionDownload}/merchant/branches/export-json-images`" method="POST">
    <input type="hidden" name="merchantId" :value="merchantIdExport" />
    <input type="hidden" name="merchant_code" :value="merchantCodeExport" />
    <input type="hidden" name="token" :value="token" />
    <input id="exportBranchesImages" type="submit" class="d-none" />
  </form>
  <div class="position-fixed top-1 end-1 z-index-2 message-success">
    <material-snackbar
      v-if="snackbar === 'danger' || snackbar === 'success'"
      title
      date
      :description="message"
      :icon="{ component: 'campaign', color: 'white' }"
      :color="snackbar"
      :close-handler="closeSnackbar"
    />
  </div>
  <material-loading :active="loaderActive" />
</template>

<script>
import MaterialSnackbar from "@/components/MaterialSnackbar.vue";
import MaterialLoading from "@/components/MaterialLoading.vue";
import MaterialButton from "@/components/MaterialButton.vue";
import MaterialInput from "@/components/MaterialInput.vue";
import SettingGeneralService from "../../services/settingGeneral.service";
import SettingStoreService from "../../services/settingStore.service";
import TokenService from "../../services/token.service";
import $ from "jquery";
import { scrollToFirstInvalid } from "@/config/utils";

export default {
  name: "settings-export-import",
  components: {
    MaterialSnackbar,
    MaterialLoading,
    MaterialButton,
    MaterialInput,
  },
  data() {
    return {
      file: null,
      fileName: "No file chosen",
      fileError: "",
      images: null,
      imagesName: "No file chosen",
      imagesError: "",
      previewResult: null,
      importResult: null,
      snackbar: null,
      message: "",
      loaderActive: false,
      actionDownload: process.env.VUE_APP_BASE_URL,
      token: TokenService.getLocalAccessToken(),
      merchantIdExport: localStorage.getItem("MERCHANT_ID", ""),
      merchantCodeExport: localStorage.getItem("MERCHANT_CODE", ""),
      branchFile: null,
      branchFileName: "No file chosen",
      branchFileError: "",
      branchImages: null,
      branchImagesName: "No file chosen",
      branchImagesError: "",
      branchPreviewResult: null,
      branchImportResult: null,
    };
  },
  computed: {
    /**
     * Only the branches worth reading about. A merchant importing its own file back has every
     * branch matching, and a table of "nothing to change" that runs off the screen hides the two
     * rows that do change.
     */
    kioskRows() {
      const branches = this.previewResult?.kiosk?.branches ?? [];

      return branches.filter((row) => row.status !== "unchanged");
    },

    kioskUnchanged() {
      const branches = this.previewResult?.kiosk?.branches ?? [];

      return branches.filter((row) => row.status === "unchanged").length;
    },

    /**
     * The v3 row sections of the preview, in the order Setting lists them, with the rows worth
     * reading kept apart from the ones that already match - the same reason kioskRows does it.
     *
     * A section the file does not carry is dropped rather than shown empty: a merchant importing
     * a file written before v3 should not be told about five sections that are not in it.
     */
    rowSections() {
      const preview = this.previewResult;

      if (!preview) {
        return [];
      }

      const sections = [
        { key: "cashback", title: "Cashback rules", section: preview.cashback },
        { key: "point", title: "Point rules", section: preview.point },
        { key: "topup_plans", title: "Top-up plans", section: preview.topup?.plans },
        { key: "topup_promotions", title: "Top-up promotions", section: preview.topup?.promotions },
        { key: "notifications", title: "Notification templates", by: "type", section: preview.notifications },
        { key: "email_templates", title: "Email campaigns", section: preview.email_templates },
        { key: "tiers", title: "Tiers", section: preview.tiers },
        { key: "vouchers", title: "Vouchers", section: preview.vouchers },
        { key: "voucher_bundles", title: "Voucher bundles", section: preview.voucher_bundles },
        { key: "discounts", title: "Discounts", section: preview.discounts },
        // A slide has no name column, so it is matched on its caption - say that rather than
        // let the heading claim a name the screen never asked the merchant for.
        { key: "slides", title: "Slider images", by: "caption", section: preview.slides },
      ];

      return sections
        .filter((entry) => entry.section && entry.section.rows.length)
        .map((entry) => ({
          by: "name",
          ...entry,
          rows: entry.section.rows.filter((row) => row.status !== "unchanged"),
          unchanged: entry.section.rows.filter((row) => row.status === "unchanged").length,
        }));
    },

    /**
     * The two sections of the branch preview, in the same shape as the settings row sections so
     * they render through the same table.
     */
    branchSections() {
      const preview = this.branchPreviewResult;

      if (!preview) {
        return [];
      }

      return [
        { key: "physical_branches", title: "Groupings", by: "name", section: preview.physical_branches },
        { key: "branches", title: "Branches", by: "branch code", section: preview.branches },
      ]
        .filter((entry) => entry.section && entry.section.rows.length)
        .map((entry) => ({
          ...entry,
          rows: entry.section.rows.filter((row) => row.status !== "unchanged"),
          unchanged: entry.section.rows.filter((row) => row.status === "unchanged").length,
        }));
    },

    /**
     * What the import actually wrote, per row section, leaving out the ones it wrote nothing for.
     */
    importedRows() {
      const result = this.importResult;

      if (!result) {
        return [];
      }

      // "rule(s)" rather than "rules", the way the branch(es) and store(s) beside it read.
      return [
        { key: "cashback_rows", title: "cashback rule(s)" },
        { key: "point_rows", title: "point rule(s)" },
        { key: "topup_plans", title: "top-up plan(s)" },
        { key: "topup_promotions", title: "top-up promotion(s)" },
        { key: "notifications", title: "notification template(s)" },
        { key: "email_templates", title: "email campaign(s)" },
        { key: "tiers", title: "tier(s)" },
        { key: "vouchers", title: "voucher(s)" },
        { key: "voucher_bundles", title: "voucher bundle(s)" },
        { key: "discounts", title: "discount(s)" },
        { key: "slides", title: "slider image(s)" },
      ]
        .map((entry) => ({ ...entry, count: result[entry.key] ?? 0 }))
        .filter((entry) => entry.count > 0);
    },
  },
  methods: {
    exportSettingsJson() {
      $("#exportSettingsJson").click();
    },

    exportSettingsImages() {
      $("#exportSettingsImages").click();
    },

    exportBranchesJson() {
      $("#exportBranchesJson").click();
    },

    exportBranchesImages() {
      $("#exportBranchesImages").click();
    },

    chooseBranchFile() {
      $("#file_import_branches_json").click();
    },

    chooseBranchImages() {
      $("#file_images_branches_zip").click();
    },

    onBranchFileChange(event) {
      const file = event.target.files[0];

      this.branchPreviewResult = null;
      this.branchImportResult = null;
      this.branchFileError = "";

      if (!file) {
        this.branchFile = null;
        this.branchFileName = "No file chosen";
        return;
      }

      if (!file.name.toLowerCase().endsWith(".json")) {
        this.branchFile = null;
        this.branchFileName = "No file chosen";
        this.branchFileError = "Only .json files exported from Sales Channel can be imported.";
        return;
      }

      this.branchFile = file;
      this.branchFileName = file.name;
    },

    onBranchImagesChange(event) {
      const file = event.target.files[0];

      this.branchImagesError = "";

      if (!file) {
        this.branchImages = null;
        this.branchImagesName = "No file chosen";
        return;
      }

      if (!file.name.toLowerCase().endsWith(".zip")) {
        this.branchImages = null;
        this.branchImagesName = "No file chosen";
        this.branchImagesError = "Only a .zip archive of icons can be uploaded here.";
        return;
      }

      this.branchImages = file;
      this.branchImagesName = file.name;
    },

    branchFormData() {
      const form = new FormData();

      form.append("file_import", this.branchFile);

      if (this.branchImages) {
        form.append("file_images", this.branchImages);
      }

      return form;
    },

    branchValid() {
      this.branchFileError = this.branchFile ? "" : "Choose a .json file to import.";

      return !this.branchFileError;
    },

    previewBranches() {
      if (!this.branchValid()) {
        return;
      }

      this.showLoader();
      this.branchImportResult = null;

      SettingStoreService.importBranchesJsonPreview(this.branchFormData()).then(
        (response) => {
          if (response.data.result.isSuccess) {
            this.branchPreviewResult = response.data.data;
          } else {
            this.branchPreviewResult = null;
            this.snackbar = "danger";
            this.message = response.data.result.message;
            this.setTimeOutNotification();
          }
          this.hideLoader();
        },
        (error) => {
          this.branchPreviewResult = null;
          this.snackbar = "danger";
          this.message = this.errorMessage(error);
          this.hideLoader();
          this.setTimeOutNotification();
        }
      );
    },

    confirmImportBranches() {
      if (!this.branchValid()) {
        return;
      }

      this.showLoader();

      SettingStoreService.importBranchesJson(this.branchFormData()).then(
        (response) => {
          if (response.data.result.isSuccess) {
            this.branchImportResult = response.data.data;
            this.branchPreviewResult = null;
            this.snackbar = "success";
            this.message = "Branches imported.";
          } else {
            this.snackbar = "danger";
            this.message = response.data.result.message;
          }
          this.hideLoader();
          this.setTimeOutNotification();
        },
        (error) => {
          this.snackbar = "danger";
          this.message = this.errorMessage(error);
          this.hideLoader();
          this.setTimeOutNotification();
        }
      );
    },

    chooseFile() {
      $("#file_import_settings_json").click();
    },

    chooseImages() {
      $("#file_images_settings_zip").click();
    },

    onFileChange(event) {
      const file = event.target.files[0];

      this.previewResult = null;
      this.importResult = null;
      this.fileError = "";

      if (!file) {
        this.file = null;
        this.fileName = "No file chosen";
        return;
      }

      if (!file.name.toLowerCase().endsWith(".json")) {
        this.file = null;
        this.fileName = "No file chosen";
        this.fileError = "Only .json files exported from Setting → General can be imported.";
        return;
      }

      this.file = file;
      this.fileName = file.name;
    },

    onImagesChange(event) {
      const file = event.target.files[0];

      this.imagesError = "";

      if (!file) {
        this.images = null;
        this.imagesName = "No file chosen";
        return;
      }

      if (!file.name.toLowerCase().endsWith(".zip")) {
        this.images = null;
        this.imagesName = "No file chosen";
        this.imagesError = "Only a .zip archive of pictures can be uploaded here.";
        return;
      }

      this.images = file;
      this.imagesName = file.name;
    },

    formData() {
      const form = new FormData();

      form.append("file_import", this.file);

      if (this.images) {
        form.append("file_images", this.images);
      }

      return form;
    },

    valid() {
      this.fileError = this.file ? "" : "Choose a .json file to import.";

      if (this.fileError) {
        scrollToFirstInvalid();
      }

      return !this.fileError;
    },

    preview() {
      if (!this.valid()) {
        return;
      }

      this.showLoader();
      this.importResult = null;

      SettingGeneralService.importSettingsJsonPreview(this.formData()).then(
        (response) => {
          if (response.data.result.isSuccess) {
            this.previewResult = response.data.data;
          } else {
            this.previewResult = null;
            this.snackbar = "danger";
            this.message = response.data.result.message;
            this.setTimeOutNotification();
          }
          this.hideLoader();
        },
        (error) => {
          this.previewResult = null;
          this.snackbar = "danger";
          this.message = this.errorMessage(error);
          this.hideLoader();
          this.setTimeOutNotification();
        }
      );
    },

    confirmImport() {
      if (!this.valid()) {
        return;
      }

      this.showLoader();

      SettingGeneralService.importSettingsJson(this.formData()).then(
        (response) => {
          if (response.data.result.isSuccess) {
            this.importResult = response.data.data;
            this.previewResult = null;
            this.snackbar = "success";
            this.message = "Settings imported.";
          } else {
            this.snackbar = "danger";
            this.message = response.data.result.message;
          }
          this.hideLoader();
          this.setTimeOutNotification();
        },
        (error) => {
          this.snackbar = "danger";
          this.message = this.errorMessage(error);
          this.hideLoader();
          this.setTimeOutNotification();
        }
      );
    },

    errorMessage(error) {
      const result = error && error.response && error.response.data ? error.response.data.result : null;

      if (result && result.message) {
        return result.message;
      }

      // No response at all: the request timed out, the connection dropped, or the server died
      // mid-import. Saying that beats surfacing whatever TypeError the axios layer threw.
      if (error && !error.response) {
        return "The server did not answer — the import may have timed out or the file may be too large. Check the API log before retrying.";
      }

      return String(error);
    },

    /** added -> "Added" */
    label(key) {
      const text = String(key).replace(/_/g, " ");

      return text.charAt(0).toUpperCase() + text.slice(1);
    },

    showLoader() {
      this.loaderActive = true;
    },

    hideLoader() {
      this.loaderActive = false;
    },

    closeSnackbar() {
      this.snackbar = null;
    },

    setTimeOutNotification() {
      setTimeout(() => {
        this.snackbar = null;
      }, 5000);
    },
  },
};
</script>

<style scoped>
.page-header .container {
  color: #222 !important;
}

/* The five row sections are five separate tables, so without a width each one sizes its own
   columns and the statuses zig-zag down the page. */
.row-section td:first-child {
  width: 260px;
}

.row-section td:nth-child(2) {
  width: 320px;
}

.s-setting > div:first-child {
  min-width: 120px;
}
</style>
